<template>
  <div class="invoice-send-wrap">
    <portal to="dash-modal">
      <InvoiceSendAdjustModal 
        v-if="showAdjustModal"
        :invoice-id="sendDetails.invoiceId"
        :mark-as-sent="markedAsSent"
        @mark-invoice-as-sent="$emit('mark-as-sent')"
        @send-invoice="sendInvoice"
        @close="showAdjustModal = false"
      />
    </portal>
    <div class="invoice-wrap-bg">
      <div class="invoice-wrap">
        <LoadingSpinner v-if="loading || isSending" :display-text="(isSending) ? ((isQuotation) ? $gettext('Sharing quotation') : $gettext('Sending invoice')): ''" />
        <b-form v-if="!loading" @submit.prevent="adjustAndSend" @keydown.enter="adjustAndSend" class="invoice-send-form gk-input-light--delete">

          <div v-if="showRecurrentWarningTxt">
            <b-alert show class="gk-alert mb-0" variant="warning">
              <div>
                {{ $gettext('Your planning has been saved as a task but if you want to send out this invoice today, you also need to manually send this draft copy.') }}
              </div>
            </b-alert>
          </div>

          <div v-if="showVerifactuCancelWarning">
            <b-alert show class="gk-alert mb-0" variant="warning">
              <div>
                {{ $gettext('This invoice has been cancelled in the AEAT. If you send a reminder, please note that it is no longer valid for tax purposes.') }}
              </div>
            </b-alert>
          </div>

          <div v-if="showPeppolWarning">
            <b-alert show class="gk-alert mb-0" variant="warning">
              <div>
                {{ $gettext('This invoice will be sent via the Peppol network.') }}
              </div>
            </b-alert>
          </div>

          <b-form-group 
            id="input-group-recipients"
            :label="$gettext('Recipients*')"
            label-for="input-recipients"
            label-class="gk-label-margin gk-modal-label"
          >
            <b-input-group>
              <EmailFormInput v-if="customerLoaded" :preselected="sendDetails.recipients" id="input-recipients" ref="email_form_input" />
            </b-input-group>

            <small tabindex="-1" id="input-group-recipients__BV_description_" class="form-text text-muted" style="font-size: 14px;">{{ $gettext('You can add multiple email addresses') }}</small>

            <EmailValidityHint variant="boxy" show-link />

            <b-form-checkbox
              id="checkbox-includeme"
              v-model="updateCC"
              @change="updateCCSave"
              name="checkbox-includeme"
            >
              <span v-if="isQuotation">{{ $gettext('Send me a copy of this Quotation') }}</span>
              <span v-else>{{ $gettext('Send me a copy of this Invoice') }}</span>
            </b-form-checkbox>

          </b-form-group>

          <b-form-group
            id="input-group-subject"
            :label="$gettext('Subject*')"
            label-for="input-subject"
            label-class="gk-label-margin gk-modal-label"
          >
            <b-form-input v-if="currentCompany.is_premium" id="input-subject" v-model="sendDetails.subject" />
            <b-form-input v-else id="input-subject" v-model="sendDetails.subject" readonly @click="$store.commit('togglePremiumModal', 'web')" />
          </b-form-group>

          <b-form-group id="input-group-message" label-class="gk-label-margin gk-modal-label" label-for="input-message">
            <template slot="label">
              <span v-translate>Message</span>
            </template>
            <b-input-group class="mb-2">
              <div class="premium-textarea-wrapper">
                <b-form-textarea id="input-message" v-model="sendDetails.message" required :placeholder="$gettext('Dear...')" rows="6" no-resize />
                
                <div class="premium-info text-blue" v-if="sendDetails.attach_mollie_payment">
                  <p>{{ $gettext('Pay this invoice directly using a payment link') }} <font-awesome-icon class="mx-2" role="button" @click="detachMolliePayment" icon="fa-solid fa-xmark" /></p>
                </div>

                <div class="premium-info text-blue" v-if="sendDetails.attach_rootline_payment">
                  <p>{{ $gettext('Pay this invoice directly using a payment link') }} <font-awesome-icon class="mx-2" role="button" @click="detachRootlinePayment" icon="fa-solid fa-xmark" /></p>
                </div>
                
                <div v-if="!currentCompany.is_premium" class="premium-info text-unassigned" id="non-premium-text" v-b-tooltip.hover.top="$gettext('You can remove this text by upgrading to Gekko Premium')">
                  <p>
                    <span>{{ emailMessage }}</span>
                  </p>
                  <p v-translate>Gekko: the friendly and free online bookkeeping tool for small business owners, <span>http://getgekko.com</span></p>
                </div>
              </div>
            </b-input-group>

            <PaymentLink v-if="!isQuotation" v-model="sendDetails" :currencyNotEuro="currencyNotEuro" :invoiceAmount="invoiceAmount" />
          </b-form-group>
          <b-form-group id="input-group-3" label-class="gk-label-margin gk-modal-label" label-for="input-3">
            <template slot="label">
              <span v-translate>Attachments</span>
            </template>

            <div class="attachments-table-wrap">
              <b-table 
                :items="fullAttachmentsList"
                :fields="documentFields"
                class="attachments-table"
                :class="['gk-table gk-table--hover gk-table--responsive gk-table--headed']"
                responsive
              >
                <template #cell(title)="row">
                  <span v-if="row.item.key === 'mainpdf'">
                    {{ row.item.title }}
                    <a class="mx-2 text-blue" v-if="!isQuotation" @click="downloadInvoice(invoice.id)" title="Download attached invoice">
                      <font-awesome-icon icon="fa-solid fa-download" />
                    </a>
                    <a class="mx-2 text-blue" v-else @click="downloadQuotation(invoice.id)" title="Download attached quotation">
                      <font-awesome-icon icon="fa-solid fa-download" />
                    </a>
                  </span>
                  <span v-else-if="row.item.key === 'hoursreport'">
                    {{ row.item.title }}
                    <a class="mx-2 text-blue" @click="downloadHoursReport" title="Download attached pdf hours report">
                      <font-awesome-icon icon="fa-solid fa-download" />
                    </a>
                  </span>
                  <span v-else>
                    {{ row.item.title }}
                  </span>
                </template>

                <template #cell(total)="row">
                  <span v-if="row.item.file_size">
                    {{ niceBytes(row.item.file_size, 1000, true) }}
                  </span>
                  <span v-else-if="row.item.key === 'mainpdf' || row.item.key === 'costsreport'">
                    {{ invoice.currency }} {{ row.item.total | formatNumber }}
                  </span>
                  <!-- <span v-else-if="row.item.key === 'hoursreport'">
                    {{ row.item.total }} {{ $gettext('hours') }}
                  </span> -->
                </template>
                
                <template #cell(actions)="row">
                  <GekkoButton 
                    :disabled="row.item.key === 'mainpdf'"
                    @click="removeDocumentFromList(row)"
                    icon="fa-solid fa-trash"
                    variant="link"
                    class="text-danger"
                  />
                </template>
              </b-table>
            </div>

            <b-form-checkbox
              id="checkbox-ubl"
              v-if="!isQuotation"
              v-show="!sendDetails.attach_ubl"
              v-model="sendDetails.attach_ubl"
              name="checkbox-ubl"
            >
              {{ $gettext('Send UBL with invoice') }}
              <font-awesome-icon id="tooltip-ubl" class="tooltip-icon tooltip-icon--question ml-1" icon="fa-regular fa-circle-question" />
              <b-tooltip :custom-class="'tooltip-padded'" target="tooltip-ubl" triggers="hover" container="#report-container">
                <div class="tooltip-text">
                  {{ $gettext('UBL (Universal Business Language) is a standard for invoices that is used across the world. It is a format used in many accountancy applications.') }}
                </div>
              </b-tooltip>
            </b-form-checkbox>

            <b-form-checkbox
              id="checkbox-attach_costs"
              v-if="attachedCosts && attachedCosts.count > 0"
              v-show="!sendDetails.attach_costs"
              v-model="sendDetails.attach_costs"
              name="checkbox-attach_costs"
            >
              {{ $gettext('Attach the cost proofs of your imported cost[s]') }}
            </b-form-checkbox>

            <b-form-checkbox
              id="checkbox-attach_hours"
              v-if="attachedHours && attachedHours > 0"
              v-show="!sendDetails.attach_hours"
              v-model="sendDetails.attach_hours"
              @change="onAttachHoursChange"
              name="checkbox-attach_hours"
            >
              {{ $gettext('Attach report of imported hours (.pdf)') }}
            </b-form-checkbox>

            <div>
              <div v-show="showDocumentSelector">
                <DocumentSelector 
                  ref="document_selector"
                  show-file-uploader
                  @documentSelected="documentSelected"
                  @showFileUploader="displayFileUploader"
                />
              </div>

              <div v-if="!showDocumentSelector" class="premium-badge--inline">
                <GekkoButton 
                  @click="displayDocumentSelector"
                  :text="$gettext('Add another file')"
                  icon="fa-solid fa-plus"
                  variant="link"
                  class="px-0"
                />
                <GkBadge 
                  icon="fa-solid fa-crown"
                  :text="$gettext('Premium')"
                  variant="base"
                  type="premium"
                />
              </div>
            </div>
          </b-form-group>

          <FileUploaderFull 
            v-if="showFileUploader"
            :document-name="$gettext('invoice') + ' ' + invoice.number"
            @document-selected="documentSelected"
          />

        </b-form>
      </div>
    </div>
  </div>

</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";

import { niceBytes, promptDownload } from "@/utils/";

import invoices from "@/mixins/invoices";

import InvoiceRecurrentService from '@/store/services/invoice.recurrent.service.js';
import InvoiceService from '@/store/services/invoice.service.js';
import QuotationService from "@/store/services/quotation.service.js"
import CompanySettingService from '@/store/services/companysetting.service.js';
import EmailMessageNotificationService from "@/store/services/email-message-notification.service.js";
import CostService from "@/store/services/cost.service.js";
import HourService from "@/store/services/hour.service.js";
import DocumentService from "@/store/services/document.service";
import TitleBar from "@/components/TitleBar";
import GekkoButton from "@/components/GekkoButton";
import PaymentLink from "@/components/payments/PaymentLink";
import EmailFormInput from "@/components/ui/EmailFormInput";
import FileUploaderFull from "@/components/documents/FileUploaderFull";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import DocumentSelector from "@/components/documents/DocumentSelector";
import InvoiceSendAdjustModal from "@/components/invoices/ui/InvoiceSendAdjustModal"
import EmailValidityHint from '@/components/EmailValidityHint'

export default {
  name: "InvoiceSend",
  mixins: [invoices],
  components: {
    TitleBar,
    GekkoButton,
    EmailFormInput,
    FileUploaderFull,
    LoadingSpinner,
    DocumentSelector,
    InvoiceSendAdjustModal,
    PaymentLink,
    EmailValidityHint
  },
  props: {
    isQuotation: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      recurrentResponse: false,
      sendDetails: {
        recipients: '',
        subject: '',
        message: '',
        attach_costs: false,
        attach_hours: false,
        attach_ubl: false,
        attach_mollie_payment: false,
        attach_rootline_payment: false
      },
      loading: true,
      customerLoaded: false,
      updateCC: false,
      invoiceNumber: null,
      isSending: false,
      today: null,
      showUpdate: null,
      showAdjustModal: false,
      attachedCosts: null,
      attachedHours: null,
      showDocumentSelector: false,
      showFileUploader: false,
      mainInvoicePDF: {
        key: 'mainpdf',
        title: '',
        total: '',
        description: ''
      },
      invoiceUBL: {
        key: 'ublfile',
        title: this.$gettext('UBL File attached'),
        description: ''
      },
      attachedHoursReport: {
        key: 'hoursreport',
        title: this.$gettext('Report of attached hours (.pdf)'),
        total: '',
        description: ''
      },
      selectedDocuments: [],
      documentFields: [
        { key: "title", label: this.$gettext('Title'), class: 'tablecol-md' },
        { key: "total", label: this.$gettext('Total / Size'), class: 'tablecol-sm' },
        { key: "description", label: this.$gettext('Description'), class: 'tablecol-md' },
        { key: "actions", label: "", tdClass: "text-right" }
      ],
      markedAsSent: false
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany",
      isPremium: 'getIsPremium',
      invoice: "invoices/getInvoice",
      storeSendDetails: 'invoices/getSendDetails',
      quotationFiles: 'invoices/getQuotationFiles',
      brandById: "brands/getBrandById",
      scheduledForToday: "invoices/getScheduledForToday"
    }),
    showRecurrentWarningTxt () {
      return this.scheduledForToday
    },
    showVerifactuCancelWarning() {
      return (this.invoice.status === 's' || this.invoice.status === 'p') && this.invoice.verifactuinvoice && 
              (this.invoice.verifactuinvoice.status == 'cancelled' || this.invoice.verifactuinvoice.status == 'cancelled_with_errors')
    },
    showPeppolWarning() {
      return !this.isQuotation && ['NL', 'BE'].includes(this.currentCompany.country) && this.invoice.peppolinvoice && this.invoice.peppolinvoice.status
    },
    emailMessage(){
      var message = this.$gettext('This email has been sent to you via Gekko on behalf of %{ company_name }.');
      message = message.replace("%{ company_name }", this.currentCompany.name);
      return message;
    },
    sendText () {
      let text = this.$gettext('Send invoice')
      return text
    },
    recipientsFullString () {
      return (this.$refs.email_form_input) ? this.$refs.email_form_input.getFullString() : ''
    },
    fullAttachmentsList () {
      const fullList = [this.mainInvoicePDF].concat(this.selectedDocuments)
      if (this.sendDetails.attach_costs ) {
        this.attachedCosts.results.forEach(cost => {
          const costReport = {
            key: 'costsreport',
            title: cost.number,
            total: cost.amount,
            description: cost.description
          }
          fullList.push(costReport)
        })
      }

      if (this.sendDetails.attach_hours) {
        fullList.push(this.attachedHoursReport)
      }

      if (this.sendDetails.attach_ubl) {
        fullList.push(this.invoiceUBL)
      }
      return fullList
    },
    currencyNotEuro () {
      return this.invoice.currency != 'EUR';
    },
    invoiceAmount () {
      return Math.max(parseFloat(this.invoice.total_amount), parseFloat(this.invoice.currency_total_amount)); 
    }
  },
  created () {
    this.setupData()
  },
  mounted () {
    document.body.classList.add('modal-on')
  },
  beforeDestroy () {
    this.sendDetails.recipients = this.recipientsFullString
    this.setSendDetails(this.sendDetails)
  },
  methods: {
    ...mapMutations ({ 
      setSendDetails: "invoices/SET_INVOICE_SEND_DETAILS",
      setCompanySettings: "main/SET_CURRENT_COMPANY_SETTINGS",
      setInvoiceStatus: 'invoices/SET_INVOICE_STATUS',
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE'
    }),
    ...mapActions ({ 
      getNextInvoiceNumber: "invoices/getNextInvoiceNumber",
      loadInvoice: 'invoices/loadInvoice',
      loadInvoiceRecurrent: "invoices/loadInvoiceRecurrent",
    }),
    close () {
      this.$emit('close')
      document.body.classList.remove('modal-on')
    },
    setupData () {
      if (this.isQuotation) {
        this.updateCC = this.currentCompany.companysettings.include_sender_in_cc_quotation
      } else {
        this.updateCC = this.currentCompany.companysettings.include_sender_in_cc
      }
      this.today = new Date().toString('yyyy-MM-dd')
      
      let invoiceId
      let invoice_id

      if (this.isQuotation) {
        invoiceId = this.$route.params['quotationId']
      } else {
        invoiceId = this.$route.params['invoiceId']
      }

      if (invoiceId && Number.isInteger(parseFloat(invoiceId))) {
        // get invoice data and set default email values
        // get recurrent information if any
        InvoiceRecurrentService.getRecurrentInformation(this.invoice.id).then((response) => {
          if (response.data.recurrent) {
            this.recurrentResponse = response.data
            invoice_id = response.data.number
          } else {
            invoice_id = invoiceId
          }
          this.getInvoiceData(invoice_id)
        }).catch((error) => {
          if (error.status === 409) {
            this.$store.commit('setAlert', {
              title: this.$gettext("Conflict error"),
              text: this.$gettext("The first scheduled invoice has already been created. Please try scheduling a new one."),
              variant: "warning",
              duration: 6
            })
          }
          this.$router.push({ name: 'Invoices' })
        })
      } else {
        this.$router.push({ name: 'Invoices' })
      }

      CostService.list({ invoice: invoiceId }).then((response) => {
        this.attachedCosts = response.data
      }).catch((error) => {
        console.log('could not retrieve costs', error)
        this.loading = false
      })

      HourService.count({ invoice: invoiceId }).then( (response) => {
        this.attachedHours = response.data
        this.attachedHoursReport.total = this.attachedHours
        this.loading = false
      }).catch((error) => {
        console.log('could not retrieve hours', error)
        this.loading = false
      })
    },
    downloadInvoice(invoice_id) {
      InvoiceService.getInvoicePDFBlob(invoice_id).then(response => {
        promptDownload(response)
      }).catch(error => console.error('Error downloading PDF:', error));
    },
    downloadQuotation (quotation_id) {
      QuotationService.getQuotationPDFBlob(quotation_id).then(response => {
        promptDownload(response)
      }).catch(error => console.error('Error downloading PDF:', error));
    },
    getInvoiceData (invoice_id) {
      this.$set(this.sendDetails, 'invoiceId', invoice_id)

      const isDefaultNumber = /^\d{4}-\d{6}$/.test(this.invoice.number)
      const backupNumber = this.invoice.number
      const currentYear = { 'year': new Date(this.today).getFullYear() }
      const data = { 'sender_company': this.currentCompany.id, 'year': currentYear }
      
      this.getNextInvoiceNumber(data).then((response) => {
        const newInvoiceNumber = response.data
        if (isDefaultNumber && currentYear != new Date(this.invoice.invoice_date).getFullYear()) {
          this.invoiceNumber = newInvoiceNumber
          this.showUpdate = true
        } else {
          this.invoiceNumber = backupNumber
          this.showUpdate = false
        }
        this.loading = false
        
        if (this.recurrentResponse && this.recurrentResponse.recurrent) {
          this.$emit('display-buttons', 'send-recurrent')
        } else {
          this.$emit('display-buttons', 'send')
        }

      })

      const invoice_amount = Math.max(parseFloat(this.invoice.total_amount), parseFloat(this.invoice.currency_total_amount))
      // this.amountLTEZero = invoice_amount <= 0;

      if (this.isQuotation) {
        this.mainInvoicePDF.title = this.$gettext('Quotation #') + ' ' + this.invoice.number
        this.mainInvoicePDF.description = this.$gettext('valid until') + ' ' + this.invoice.due_date
      } else {
        this.mainInvoicePDF.title = this.$gettext('Invoice #') + ' ' + this.invoice.number
        if (!this.invoice.paid_up_front) {
          this.mainInvoicePDF.description = this.$gettext('due on') + ' ' + this.invoice.due_date
        }
      }

      if (!this.invoice.due_date) this.mainInvoicePDF.description = ''

      this.mainInvoicePDF.total = invoice_amount

      if (!this.sendDetails.attach_ubl) {
        this.$set(this.sendDetails, 'attach_ubl', this.currentCompany.companysettings.attach_ubl_to_invoice)
      }
      if (!this.isQuotation) {
        this.$set(this.sendDetails, 'message', this.currentCompany.companysettings.default_invoice_email_message)
      }

      // if this is a recurrent invoice scheduledForToday 
      if (this.scheduledForToday)  {
        // need to refresh invoice list
        this.$store.commit('invoices/REFRESH_INVOICE_LIST')
      }

      let sent_from = this.currentCompany.name
      if (this.invoice.brand) {
        const invoice_brand = this.brandById(this.invoice.brand)
        if (invoice_brand && invoice_brand.name) {
          sent_from = invoice_brand.name
        }
      }

      this.setCustomerData(this.invoice.recipient_customer)

      if (this.sendDetails.documents) {
        DocumentService.getDocumentsByIdList({id_list: `[${this.sendDetails.documents}]`}).then((response) => {
          response.data.forEach((document) => {
            this.documentSelected(document)
          })
        })
      }

      if (!this.sendDetails.subject) {
        this.$set(this.sendDetails, 'subject', ((this.isQuotation) ? this.$gettext('Quotation') : this.$gettext('Invoice'))  + ' ' + this.invoice.number + ' ' + this.$gettext('from') + ' ' + sent_from)
        if (
          (this.isQuotation && this.invoice.status !== 'draft') || 
          (!this.isQuotation && this.invoice.status !== 'd')
        ) {
          this.$set(this.sendDetails, 'subject', this.$gettext('Reminder') + ' ' + this.invoice.number + ' ' + this.$gettext('from') + ' ' + sent_from)
        }
      }

      if (this.isQuotation) {
        this.setQuotationDefaultFiles()
      }
    },
    setQuotationDefaultFiles () {
      this.selectedDocuments = this.selectedDocuments.concat(this.quotationFiles)
    },
    setCustomerData (customer) {
      if (this.$route.params['emailMessageId']) {
        EmailMessageNotificationService.retrieve(this.$route.params['emailMessageId']).then((response) => {
          this.$set(this.sendDetails, 'recipients', response.data.recipients)
          this.$set(this.sendDetails, 'message', response.data.message)

          if (this.recurrentResponse && this.recurrentResponse.recurrent) {
            this.$set(this.sendDetails, 'invoiceId', this.recurrentResponse.number)
          }
          this.$set(this.sendDetails, 'subject', response.data.subject)
          this.$set(this.sendDetails, 'attach_ubl', response.data.attach_ubl)
          this.$set(this.sendDetails, 'attach_costs', response.data.attach_costs)
          this.$set(this.sendDetails, 'attach_hours', response.data.attach_hours)
          this.$set(this.sendDetails, 'documents', response.data.documents)
        })
      } else {
        if (this.recurrentResponse && this.recurrentResponse.recurrent) {
          this.$set(this.sendDetails, 'invoiceId', this.recurrentResponse.number)
          this.$set(this.sendDetails, 'recipients', this.recurrentResponse.emails)
          if (this.recurrentResponse.invoice_message) {
            this.$set(this.sendDetails, 'message', this.recurrentResponse.invoice_message)
          }
          this.$set(this.sendDetails, 'subject', this.recurrentResponse.recurrent_invoice_data.subject)
          this.$set(this.sendDetails, 'attach_ubl', this.recurrentResponse.recurrent_invoice_data.attach_ubl)
          this.$set(this.sendDetails, 'attach_costs', this.recurrentResponse.recurrent_invoice_data.attach_costs)
          this.$set(this.sendDetails, 'attach_hours', this.recurrentResponse.recurrent_invoice_data.attach_hours)
          this.$set(this.sendDetails, 'documents', this.recurrentResponse.recurrent_invoice_data.documents 
            ? this.recurrentResponse.recurrent_invoice_data.documents : '')
        } else {
          this.sendDetails.recipients = customer.email
        }
      }

      if (this.storeSendDetails) {
        // recover previous stored sendDetails
        this.$set(this.sendDetails, 'recipients', this.storeSendDetails.recipients)
        this.$set(this.sendDetails, 'subject', this.storeSendDetails.subject)
        this.$set(this.sendDetails, 'message', this.storeSendDetails.message)
        this.$set(this.sendDetails, 'attach_mollie_payment', this.storeSendDetails.attach_mollie_payment)
        this.$set(this.sendDetails, 'attach_rootline_payment', this.storeSendDetails.attach_rootline_payment)
        this.$set(this.sendDetails, 'attach_ubl', this.storeSendDetails.attach_ubl)
        this.$set(this.sendDetails, 'attach_hours', this.storeSendDetails.attach_hours)
        this.$set(this.sendDetails, 'attach_costs', this.storeSendDetails.attach_costs)
        this.$set(this.sendDetails, 'documents', this.storeSendDetails.documents)
      }

      this.customerLoaded = true
    },
    adjustAndSend (ev) {
      if (ev && ev.key && ev.target.nodeName !== 'TEXTAREA') {
        ev.preventDefault()
        return
      }
      if (!ev || (ev && !ev.key)) { // avoid submitting on key events
        this.$set(this.sendDetails, 'recipients', this.recipientsFullString)

        if (this.selectedDocuments.length > 0) {
          this.$set(this.sendDetails, 'documents', this.selectedDocuments.map(doc => doc.id).join(','))
        }

        if (!this.sendDetails.recipients || this.sendDetails.recipients === '') {
          this.$store.commit('setAlert', {
            title: this.$gettext("Recipients is empty"),
            text: this.$gettext("You need input at least one valid email address."),
            variant:"warning",
            duration: 4
          })
        } else {
          if (!this.isQuotation) {
            this.showAdjustModal = true
            this.markedAsSent = false
          } else {
            this._sendQuotation();
          }
        }
      }
    },
    sendInvoice (params) {
      this.showAdjustModal = false
      this._sendInvoice(params.new_number, params.old_number)
    },
    _sendQuotation () {
      if (this.formIsValid()) {
        this.isSending = true
        const prepared_details = Object.assign({}, this.sendDetails)
        
        prepared_details.id = prepared_details.invoiceId
        delete prepared_details.invoiceId
        delete prepared_details.attach_costs
        delete prepared_details.attach_hours

        QuotationService.send(prepared_details).then(() => {
          if (window.mixpanel) {
            window.mixpanel.track("Quotations: send",{
              'recipient': this.sendDetails.recipients,
              'quotation number': this.sendDetails.subject.split(' ')[0],
              'has_attached_documents': (this.sendDetails.documents) ? this.sendDetails.documents.length : '',
              'has_attached_costs': this.sendDetails.attach_costs,
              'has_attached_hours': this.sendDetails.attach_hours,
              'has_attached_ubl': this.sendDetails.attach_ubl
            })
          }
          this.isSending = false
          this.$store.commit('setAlert', {
            title: this.$gettext('Quotation sent'),
            text: this.$gettext('Quotation sent successfully'),
            variant: 'primary',
            duration: 4
          })
          this.$emit('invoice-updated')
          this.$router.push({ name: 'QuotationView', params: { quotationId: this.invoice.id } })
          delete this.sendDetails.errors
        }).catch((error) => {
          this.isSending = false
          if (error && error.status === 429) {
            this.$store.commit('setAlert', {
              title: this.$gettext("Quotation not sent"),
              text: this.$gettext("You need to wait before sending more quotations."),
              variant:"warning",
              duration: 4
            });
            if (!this.currentCompany.is_premium) {
              this.$store.commit('togglePremiumModal', 'web_email_throttle')
            }
          } else if (error && error.status === 500) {
            this.sendDetails.error = this.$gettext('There were some problems sending the quotation. Please try again later.')
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.sendDetails.error,
              variant: "danger",
              duration: 4
            })
          } else {
            this.sendDetails.errors = error.data
          }
          if (this.sendDetails.errors && this.sendDetails.errors.recipients) {
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.sendDetails.errors.recipients[0],
              variant: "danger",
              duration: 4
            })
          } else {
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.sendDetails.errors,
              variant: "danger",
              duration: 4
            })
          }
          console.error('Sending quotations error', { errors: error, details: this.sendDetails })
        })
      }
        
    },
    _sendInvoice (new_number, old_number) {
      this.setWaitingForResponse(true)

      if (new_number && old_number) {
        const regenerated_old_name = old_number + ' ' + this.$gettext('from') + ' ' + this.currentCompany.name
        if (!this.currentCompany.is_premium || this.sendDetails.subject === regenerated_old_name) {
          this.$set(this.sendDetails, 'subject', new_number + ' ' + this.$gettext('from') + ' ' + this.currentCompany.name)
        }
        this.$set(this.invoice, 'number', new_number)
      }
      if (this.formIsValid()) {
        this.isSending = true
        if(!this.currentCompany.is_premium && this.sendDetails.attach_hours) {
          this.$set(this.sendDetails, 'attach_costs', false)
          this.sendDetails.error = this.$gettext("You can't attach hours if you are not a premium member.")
        }
        // Set to false if null or undefined
        if (!this.sendDetails.attach_mollie_payment) this.sendDetails.attach_mollie_payment = false
        if (!this.sendDetails.attach_rootline_payment) this.sendDetails.attach_rootline_payment = false
        InvoiceService.send(this.sendDetails).then(() => {
          if (window.mixpanel) {
            window.mixpanel.track("Invoices: send",{
              'recipient': this.sendDetails.recipients,
              'invoice number': this.sendDetails.subject.split(' ')[0],
              'has_attached_documents': (this.sendDetails.documents) ? this.sendDetails.documents.length : '',
              'has_attached_costs': this.sendDetails.attach_costs,
              'has_attached_hours': this.sendDetails.attach_hours,
              'has_attached_ubl': this.sendDetails.attach_ubl
            })
          }
          this.isSending = false
          this.$store.commit('setAlert', {
            title: this.$gettext('Invoice sent'),
            text: this.$gettext('Invoice sent successfully'),
            variant: 'primary',
            duration: 4
          })

          if (!this.scheduledForToday) { 
            // Navigating to InvoiceView will change InvoiceID in case of "scheduleForToday" and that will trigger a watch on invoiceMasterModal, 
            // to avoid calling twice setupData, this emit will not be fired when "scheduleForToday"
            this.$emit('invoice-updated')
          }

          this.$router.push({ name: 'InvoiceView', params: { invoiceId: this.sendDetails.invoiceId } })

          delete this.sendDetails.errors
        }).catch((error) => {
          this.isSending = false
          // null peppolinvoice.status is equivalent to not having peppol enabled
          if ((this.invoice.verifactuinvoice || this.invoice.peppolinvoice.status) && error && [490, 491, 492, 493].includes(error.status)) {
            if (this.scheduledForToday) {
              error.status = null // avoid showing link to edit invoice, will handle the redirect automatically
              error.recurrentInvoiceId = this.recurrentResponse.recurrent_invoice_data.id
              this.$emit('invoice-error-message', error)
              this.deleteRecurrentInvoice(this.recurrentResponse.recurrent_invoice_data)
            } else {
              this.$emit('invoice-error-message', error)
            }
          } else if (error && error.status === 429) {
              this.$store.commit('setAlert', {
                  title: this.$gettext("Invoice not sent"),
                  text: this.$gettext("You need to wait before sending more invoices."),
                  variant:"warning",
                  duration: 4
              });
              if (!this.currentCompany.is_premium) {
                this.$store.commit('togglePremiumModal', 'web_email_throttle')
              }
          } else if (error && error.status === 500) {
            this.sendDetails.error = this.$gettext('There were some problems sending the invoice. Please try again later.')
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.sendDetails.error,
              variant: "danger",
              duration: 4
            })
          } else if (error && error.status === 412) {
            // handled above
          } else { // 400 Bad request
            this.sendDetails.errors = error.data
          }

          if (error.status !== 412) { // avoid showing the alert twice, as it is already shown in /store/services/axiosConfig.js
            if (this.sendDetails.errors && this.sendDetails.errors.recipients) {
              this.$store.commit('setAlert', {
                title: this.$gettext('Error'),
                text: this.sendDetails.errors.recipients[0],
                variant: "danger",
                duration: 4
              })
            } else {
              this.$store.commit('setAlert', {
                title: this.$gettext('Error'),
                text: this.sendDetails.errors,  // does not show if this is empty
                variant: "danger",
                duration: 4
              })
            }
          }
          console.error('Sending invoices error', { errors: error, details: this.sendDetails })
        }).finally(() => {
          this.setWaitingForResponse(false)
        })
      }
    },
    deleteRecurrentInvoice (invoice) {
      const delete_invoice = Object.assign({}, invoice)
      delete_invoice.active = false
      delete_invoice.company_profile = this.currentCompany.id
      delete_invoice.created_by = invoice.created_by.id
      if (invoice.days.length == 0) {
        delete_invoice.days = ''
      } else {
        delete_invoice.days = invoice.days.join()
      }
      delete_invoice.invoice = invoice.invoice
      InvoiceRecurrentService.update(delete_invoice.id, delete_invoice).then((response) => {
        this.$store.commit('invoices/REFRESH_INVOICE_RECURRENT_LIST')
        setTimeout(() => {
          this.$router.push({ name: 'InvoiceEdit', params: { invoiceId: response.data.invoice + 1} })
        }, 3000)
      })
    },
    formIsValid () {
      return !(!this.sendDetails.recipients) && !(!this.sendDetails.subject)
    },
    documentSelected (item) {
      this.showDocumentSelector = false
      this.selectedDocuments.push(item)
    },
    removeDocumentFromList(data) {
      switch (data.item.key) {
        case 'ublfile':
          this.$set(this.sendDetails, 'attach_ubl', false)
          break;
        case 'costsreport':
          this.$set(this.sendDetails, 'attach_costs', false)
          break;
        case 'hoursreport':
          this.$set(this.sendDetails, 'attach_hours', false)
          break;
        default:
          if (this.isQuotation) {
            this.selectedDocuments = this.selectedDocuments.filter((item) => item.id != data.item.id);
          } else {
            this.selectedDocuments = this.selectedDocuments.filter((item) => item.id != data.item.id);
            this.$refs.document_selector.items.splice(0, 0, data.item);
          }
          break;
      }
    },
    displayDocumentSelector () {
      if (this.currentCompany.is_premium || this.isQuotation) {
        this.showFileUploader = false
        this.showDocumentSelector = true
      } else {
        this.$store.commit('togglePremiumModal', 'web_documents_addtoinvoice')
      }
    },
    displayFileUploader () {
      this.showFileUploader = true
      this.showDocumentSelector = false
    },
    detachMolliePayment () {
      this.$set(this.sendDetails, 'attach_mollie_payment', false)
    },
    detachRootlinePayment() {
      this.$set(this.sendDetails, 'attach_rootline_payment', false)
    },
    updateCCSave () {
      CompanySettingService.partialUpdate(this.currentCompany.companysettings.id, {
        'include_sender_in_cc': this.updateCC
      }).then((response) => {
        // update stored company settings
        this.setCompanySettings(response.data)
      })
    },
    /**
     * Alias for utils/niceBytes
     */
    niceBytes (size, base, unit = false) {
      return niceBytes(size, base, unit)
    },
    downloadHoursReport () {
      let filters = {
        invoice: this.invoice.id,
        attached_to_invoice: true,
        company_profile: this.currentCompany.id,
        rounding_precision: this.currentCompany.hours_rounding_precision
      }

      HourService.getReportPDFBlob(filters).then(_ => {
        const alert_text = this.$gettext('Your download is being prepared, check your downloads page to download the file.')
        this.$store.commit('setAlert', {title: this.$gettext('Hours Report Download'), text: alert_text, variant: 'info', duration: 4})
        if (this.$route.name === "ControlPanelInvoices") {
          this.$router.push({ name: 'ControlPanelFiles' })
        } else {
          this.$router.push({ name: 'Files' })
        }
      })
      .catch(error => {
        if (error.status === 429) {
          const alert_text = this.$gettext('You\'ve reached your download limit for today.')
          this.$store.commit('setAlert', {title: this.$gettext('Hours Report Download'), text: alert_text, variant: 'danger', duration: 4})
          this.$store.commit('togglePremiumModal', 'web_files_download_limit')
        }
      })
    },
    onAttachHoursChange() {
      if (this.sendDetails.attach_hours && !this.isPremium) {
        this.$set(this.sendDetails, 'attach_hours', false)
        this.$store.commit('togglePremiumModal', 'web_documents_addtoinvoice')
      }
    }
  }
};
</script>

<style lang="scss" scoped>

</style>

<style lang="scss">
.invoice-send-wrap {
  .invoice-wrap-bg {
    padding: 0 1rem;
    @media (min-width: '992px') {
      min-height: 100%;
      .invoice-wrap {
        min-height: 100vh;
        max-width: 1200px;
        margin: 0 auto;
        padding: 2rem 0;
      }
    }
  }
}

.invoice-send-form {
  padding-bottom: 2rem;

  display: flex;
  flex-direction: column;
  gap: 2rem;

  .form-group {
    margin-bottom: 0;
  }
  
  .attachments-table-wrap {
    border-radius: $radiusLg;
    box-shadow: $filtersShadow;
    overflow: hidden;
    overflow-x: auto;

    margin-bottom: 1rem;
    .attachments-table {
      background: #f4f6f5;
      thead {
        background: #fff;
        text-transform: none;
        color: $colorTextBase;
      }
      td:nth-child(1){ width: 40%; }
      td:nth-child(2){ width: 15%; }
      td:nth-child(3){ width: 40%; }
      td:nth-child(4){ width: 5%; }
    }
  }
  .document-selector-table-container {
    border-radius: $radiusLg;
    box-shadow: $filtersShadow;
    overflow: hidden;
    overflow-x: auto;
    .document-table {
      border-radius: $radiusLg;
    }
  }

}
</style>